import React from "react";
import {
  Container,
  Text,
  Column,
  Spacing,
  Row,
  DatePicker,
  TimeRangePicker,
  TimeRangeValue,
  SegmentedControl,
  Scrollable,
  Pressable,
  TimePicker,
  cssVariables,
} from "../components";
import { format, addMinutes } from "date-fns";
import {
  parseTime,
  setSameTime,
  toTime,
  MINUTES_IN_ONE_HOUR,
} from "../lib/datetime";
import { DateRangePicker } from "../components/date_range_picker";
import { parseDay, toDay, setSameDay } from "../lib/datetime";
import { getDurationInHours } from "../lib/datetime";

import { useMediaQuery } from "../hooks/use_media_query";
import { BookingType } from "../data";
import { isSameDay } from "../offering/date_utils";
import { Button } from "../offering/button";

export interface HourlyDateTimeValue {
  bookingType: BookingType.Hourly;
  date?: string;
  time?: string;
  duration?: string;
}

export interface DailyDateTimeValue {
  bookingType: BookingType.Daily;
  startDate?: string;
  endDate?: string;
  time?: string | null;
}

export type DateTimeValue = HourlyDateTimeValue | DailyDateTimeValue;

export interface DateTimePickerProps {
  onChange?: (value?: DateTimeValue) => void;
  value?: DateTimeValue | null;
  initialBookingType?: BookingType;
  showSegmentedControl?: boolean;
  askForStartTime?: boolean;
  fixedDate?: boolean;
  minDuration?: number; // in minutes
  name?: string; // name of the space
  minStartTime?: (date: Date) => string;
  maxEndTime?: (date: Date) => string;
  isBlockedDate?: (date: Date, bookingType: BookingType) => boolean;
  isOutsideRange?: (date: Date) => boolean;
  isStartTimeBlocked?: (time: Date) => boolean;
  isEndTimeBlocked?: (time: Date, selectedStartTime: Date) => boolean;

  // HourlyDateTimeValue specific props
  onDateChange?: (date: Date) => void;
  keepTimeOnDateChange?: boolean;

  allowBypassBlockedDates?: boolean;
}

export function DateTimePicker(props: DateTimePickerProps) {
  const { device } = useMediaQuery();

  return (
    <DateTimePickerProvider {...props}>
      {device === "mobile" ? (
        <DateTimePickerMobile />
      ) : (
        <DateTimePickerDesktop />
      )}
    </DateTimePickerProvider>
  );
}

function DateTimePickerDesktop() {
  const { value, showSegmentedControl } = useDateTimePicker();
  const { error, onBookingTypeChange, onSubmit, onReset } = useDateTimePicker();

  return (
    <Container className="dashboard-date-picker__dialog">
      {showSegmentedControl && (
        <SegmentedControl
          testID="booking-type"
          value={value?.bookingType || BookingType.Hourly}
          onChange={onBookingTypeChange}
          options={[
            { value: BookingType.Hourly, label: "Hourly" },
            { value: BookingType.Daily, label: "Daily" },
          ]}
        />
      )}
      {value?.bookingType === BookingType.Daily ? (
        <DailyPickerDesktop />
      ) : (
        <HourlyPickerDesktop />
      )}
      <Row alignItems="center">
        <Container flex={1}>
          <Row alignItems="center" justifyContent="space-between">
            <Container>
              {error && (
                <>
                  <Text color="error">{error}</Text>
                  <Spacing height={8} />
                </>
              )}
              <Button appearance="outline" onPress={onReset} title="Clear" />
            </Container>
            <Container>
              <Button
                testID="save-date-time-picker"
                onPress={onSubmit}
                title="Save"
              />
            </Container>
          </Row>
        </Container>
      </Row>
    </Container>
  );
}

function HourlyPickerDesktop() {
  const {
    minDuration,
    isBlockedDate,
    isStartTimeBlocked,
    isEndTimeBlocked,
    isOutsideRange,
    minStartTime,
    maxEndTime,
    value,
    allowBypassBlockedDates,
  } = useDateTimePicker();
  const { timeRange, date, handleDateChange, handleTimeRangeChange } =
    useHourlyDateTimeValueHandlers();

  return (
    <Row>
      <Container flex={1}>
        <DatePicker
          value={date}
          onChange={handleDateChange}
          isOutsideRange={isOutsideRange}
          isBlocked={(date) => {
            if (isBlockedDate && value?.bookingType) {
              return isBlockedDate(date, value.bookingType);
            }

            return false;
          }}
          allowBypassBlockedDates={allowBypassBlockedDates}
        />
      </Container>
      <Spacing width={24} />
      <Container flex={1}>
        <Text fontSize="sm" color="muted">
          Start time → End time
        </Text>
        <Spacing height={16} />
        <TimeRangePicker
          date={date}
          value={timeRange}
          onChange={handleTimeRangeChange}
          minStartTime={minStartTime}
          maxEndTime={maxEndTime}
          minDuration={minDuration}
          isStartTimeBlocked={isStartTimeBlocked}
          isEndTimeBlocked={isEndTimeBlocked}
        />
      </Container>
    </Row>
  );
}

function DailyPickerDesktop() {
  const {
    isBlockedDate,
    isOutsideRange,
    name,
    fixedDate,
    value,
    askForStartTime,
    allowBypassBlockedDates,
  } = useDateTimePicker();
  const {
    interval,
    handleDateChange,
    time,
    handleTimeChange,
    startTime,
    endTime,
  } = useDailyDateTimeValueHandlers();

  return (
    <>
      <Row>
        <Container flex={1}>
          {fixedDate ? (
            <Text>{displayDateTime(value)}</Text>
          ) : (
            <DateRangePicker
              value={interval}
              numberOfMonths={askForStartTime ? 1 : 2}
              onChange={handleDateChange}
              isOutsideRange={isOutsideRange}
              isBlocked={(date) => {
                if (isBlockedDate && value?.bookingType) {
                  return isBlockedDate(date, value.bookingType);
                }

                return false;
              }}
              allowBypassBlockedDates={allowBypassBlockedDates}
            />
          )}
        </Container>
        {askForStartTime && (
          <>
            <Spacing width={24} />
            <Container flex={1}>
              <Text fontSize="sm" color="muted">
                Meeting start time
              </Text>
              <Spacing height={4} />
              <TimePicker
                testID="start-time"
                value={time}
                placeholder="Start time"
                onChange={handleTimeChange}
                disabled={!interval}
                startTime={startTime}
                endTime={endTime}
              />
              <Spacing height={16} />
              {startTime && endTime && name && (
                <Text fontSize="sm">
                  {name} is available from {format(startTime, "h a")} to{" "}
                  {format(endTime, "h a")} when booked for the day
                </Text>
              )}
            </Container>
          </>
        )}
      </Row>
      {!askForStartTime && startTime && endTime && name && (
        <Text fontSize="sm">
          {name} is available from {format(startTime, "h a")} to{" "}
          {format(endTime, "h a")} when booked for the day
        </Text>
      )}
    </>
  );
}

function DateTimePickerMobile() {
  const { value, showSegmentedControl, onBookingTypeChange } =
    useDateTimePicker();

  return (
    <Column expanded>
      {showSegmentedControl && (
        <>
          <SegmentedControl
            testID="booking-type"
            value={value?.bookingType || BookingType.Hourly}
            onChange={onBookingTypeChange}
            options={[
              { value: BookingType.Hourly, label: "Hourly" },
              { value: BookingType.Daily, label: "Daily" },
            ]}
          />
          <Spacing height={16} />
        </>
      )}
      {value?.bookingType === BookingType.Daily ? (
        <DailyPickerMobile />
      ) : (
        <HourlyPickerMobile />
      )}
    </Column>
  );
}

function HourlyPickerMobile() {
  const {
    minDuration,
    error,
    onSubmit,
    isBlockedDate,
    isStartTimeBlocked,
    isEndTimeBlocked,
    isOutsideRange,
    minStartTime,
    onReset,
    value,
    maxEndTime,
    allowBypassBlockedDates,
  } = useDateTimePicker();
  const { timeRange, date, handleDateChange, handleTimeRangeChange } =
    useHourlyDateTimeValueHandlers();

  return (
    <>
      <Container flex={1}>
        <Scrollable direction="vertical">
          <DatePicker
            value={date}
            onChange={handleDateChange}
            isOutsideRange={isOutsideRange}
            orientation="vertical"
            numberOfMonths={3}
            isBlocked={(date) => {
              if (isBlockedDate && value?.bookingType) {
                return isBlockedDate(date, value.bookingType);
              }

              return false;
            }}
            allowBypassBlockedDates={allowBypassBlockedDates}
          />
        </Scrollable>
      </Container>
      <Container
        paddingTop={16}
        borderTopWidth={1}
        borderColor={cssVariables.borderColorDefault}
      >
        {error && (
          <>
            <Text color="error">{error}</Text>
            <Spacing height={16} />
          </>
        )}
        <TimeRangePicker
          date={date}
          value={timeRange}
          onChange={handleTimeRangeChange}
          minStartTime={minStartTime}
          maxEndTime={maxEndTime}
          minDuration={minDuration}
          isStartTimeBlocked={isStartTimeBlocked}
          isEndTimeBlocked={isEndTimeBlocked}
        />
        <Spacing height={16} />
        <Row justifyContent="space-between" alignItems="center">
          <Pressable onPress={onReset}>
            <Text>Clear</Text>
          </Pressable>
          <Spacing width={16} />
          <Button onPress={onSubmit} title="Save" />
        </Row>
      </Container>
    </>
  );
}

function DailyPickerMobile() {
  const {
    error,
    onSubmit,
    onReset,
    isBlockedDate,
    isOutsideRange,
    name,
    value,
    askForStartTime,
    allowBypassBlockedDates,
  } = useDateTimePicker();
  const {
    interval,
    handleDateChange,
    time,
    handleTimeChange,
    startTime,
    endTime,
  } = useDailyDateTimeValueHandlers();

  return (
    <>
      <Container flex={1}>
        <Scrollable direction="vertical">
          <DateRangePicker
            value={interval}
            onChange={handleDateChange}
            orientation="vertical"
            numberOfMonths={2}
            isBlocked={(date) => {
              if (isBlockedDate && value?.bookingType) {
                return isBlockedDate(date, value.bookingType);
              }

              return false;
            }}
            isOutsideRange={isOutsideRange}
            allowBypassBlockedDates={allowBypassBlockedDates}
          />
        </Scrollable>
      </Container>
      <Container
        paddingTop={16}
        borderTopWidth={1}
        borderColor={cssVariables.borderColorDefault}
      >
        {askForStartTime && (
          <>
            <Text fontSize="sm" color="muted">
              Meeting start time
            </Text>
            <Spacing height={4} />
            <TimePicker
              testID="start-time"
              value={time}
              onChange={handleTimeChange}
              disabled={!interval}
              placeholder="Start time"
              startTime={startTime}
              endTime={endTime}
            />
            <Spacing height={16} />
          </>
        )}
        {startTime && endTime && (
          <Text fontSize="sm">
            {name} is available from {format(startTime, "h a")} to{" "}
            {format(endTime, "h a")} when booked for the day
          </Text>
        )}
        <Spacing height={16} />
        {error && (
          <>
            <Text color="error">{error}</Text>
            <Spacing height={16} />
          </>
        )}
        <Row justifyContent="space-between" alignItems="center">
          <Pressable onPress={onReset}>
            <Text>Clear</Text>
          </Pressable>
          <Spacing width={16} />
          <Button onPress={onSubmit} title="Save" />
        </Row>
      </Container>
    </>
  );
}

function useHourlyDateTimeValueHandlers() {
  const {
    value,
    onChange = () => {},
    onDateChange = () => {},
    keepTimeOnDateChange,
  } = useDateTimePicker();

  if (value?.bookingType !== BookingType.Hourly) {
    throw new Error("Invalid booking type");
  }
  const { date, time, duration } = value;

  React.useEffect(() => {
    if (date) {
      onDateChange(parseDay(date));
    }
  }, [date, onDateChange]);

  const handleDateChange = React.useCallback(
    (value: Date) => {
      if (!keepTimeOnDateChange) {
        onChange({
          bookingType: BookingType.Hourly,
          date: toDay(value),
        });
      } else {
        onChange({
          bookingType: BookingType.Hourly,
          date: toDay(value),
          time,
          duration,
        });
      }
      onDateChange(value);
    },
    [time, duration, keepTimeOnDateChange, onChange, onDateChange]
  );

  const handleTimeRangeChange = React.useCallback(
    (value: TimeRangeValue) => {
      onChange({
        bookingType: BookingType.Hourly,
        date,
        time: value.startTime && toTime(value.startTime),
        duration:
          value.startTime &&
          value.endTime &&
          String(getDurationInHours(value.startTime, value.endTime)),
      });
    },
    [date, onChange]
  );

  const parsedDate = date ? parseDay(date) : undefined;
  const parsedTime = time ? parseTime(time) : undefined;
  const startTime =
    parsedDate && parsedTime ? setSameTime(parsedDate, parsedTime) : undefined;
  const endTime =
    startTime && duration && !Number.isNaN(Number(duration))
      ? addMinutes(startTime, Number(duration) * 60)
      : undefined;

  const timeRange = startTime ? { startTime, endTime } : undefined;

  return {
    timeRange,
    date: parsedDate,
    handleDateChange,
    handleTimeRangeChange,
  };
}

function useDailyDateTimeValueHandlers() {
  const {
    value,
    onChange = () => {},
    minStartTime,
    maxEndTime,
  } = useDateTimePicker();

  if (value?.bookingType !== BookingType.Daily) {
    throw new Error("Invalid booking type");
  }

  const handleDateChange = React.useCallback(
    (interval: Interval) => {
      onChange({
        bookingType: BookingType.Daily,
        startDate: toDay(interval.start as Date),
        endDate: toDay(interval.end as Date),
        time: value.time,
      });
    },
    [value, onChange]
  );

  const handleTimeChange = React.useCallback(
    (time: Date) => {
      onChange({
        bookingType: BookingType.Daily,
        startDate: value.startDate,
        endDate: value.endDate,
        time: toTime(time),
      });
    },
    [value, onChange]
  );

  const parsedStartDate = value.startDate
    ? parseDay(value.startDate)
    : undefined;
  const parsedEndDate = value.endDate ? parseDay(value.endDate) : undefined;

  const interval =
    parsedStartDate && parsedEndDate
      ? { start: parsedStartDate, end: parsedEndDate }
      : undefined;

  const time = value.time ? parseTime(value.time) : undefined;

  const startTime =
    interval && minStartTime
      ? parseTime(minStartTime(interval?.start))
      : undefined;
  const endTime =
    interval && maxEndTime ? parseTime(maxEndTime(interval?.start)) : undefined;

  return {
    interval,
    time,
    handleDateChange,
    handleTimeChange,
    startTime,
    endTime,
  };
}

export function parseHours(date: string, time: string, duration: string) {
  const day = parseDay(date);
  const startTime = setSameDay(parseTime(time), day);
  const endTime = setSameDay(
    addMinutes(startTime, Number(duration) * MINUTES_IN_ONE_HOUR),
    day
  );

  return { day, startTime, endTime };
}

export function displayDateTime(
  value?: DateTimeValue | null,
  fallback?: string
): string {
  if (!value) {
    return fallback || "When";
  }

  if (
    value.bookingType === BookingType.Hourly &&
    value.date &&
    value.time &&
    value.duration
  ) {
    const { date, time, duration } = value;
    const { day, startTime, endTime } = parseHours(date, time, duration);

    return `${format(day, "MMM do")}, ${format(startTime, "h:mm a")} → ${format(
      endTime,
      "h:mm a"
    )}`;
  }

  if (
    value.bookingType === BookingType.Daily &&
    value.startDate &&
    value.endDate
  ) {
    const { startDate, endDate } = value;
    const start = parseDay(startDate);
    const end = parseDay(endDate);

    const day = isSameDay(start, end)
      ? `${format(start, "MMM do")}`
      : `${format(start, "MMM do")} → ${format(end, "MMM do")}`;

    return `${day}${
      value.time ? ` (${format(parseTime(value.time), "h:mm a")})` : ""
    }`;
  }

  return fallback || "When";
}

export const formatOrdersDateRange = (value: DailyDateTimeValue) => {
  if (!value.startDate || !value.endDate) {
    return "";
  }
  const start = parseDay(value.startDate);
  const end = parseDay(value.endDate);
  return isSameDay(start, end)
    ? `${format(start, "MMM d, yyyy")}`
    : `${format(start, "MMM d")} → ${format(end, "MMM d, yyyy")}`;
};

interface DateTimePickerContext extends DateTimePickerProps {
  onSubmit: () => void;
  onReset: () => void;
  onBookingTypeChange: (bookingType: BookingType) => void;
  error: string | null;
}

const DateTimePickerContext = React.createContext<DateTimePickerContext>({
  value: {
    bookingType: BookingType.Hourly,
  },
  onChange: () => {},
  onBookingTypeChange: () => {},
  onSubmit: () => {},
  onReset: () => {},
  error: null,
});

interface DateTimePickerProviderProps extends DateTimePickerProps {
  children?: React.ReactNode;
}

function DateTimePickerProvider(props: DateTimePickerProviderProps) {
  const {
    children,
    onChange = () => {},
    value,
    initialBookingType,
    onDateChange = () => {},
    askForStartTime,
    ...restProps
  } = props;
  const [error, setError] = React.useState<string | null>(null);
  const [internalValue, setInternalValue] = React.useState<DateTimeValue>(
    () => {
      if (value) {
        return value;
      }

      return {
        bookingType: initialBookingType || BookingType.Hourly,
      };
    }
  );

  const handleChange = React.useCallback(
    (value?: DateTimeValue | null) => {
      if (value) {
        setInternalValue(value);
      } else {
        setInternalValue({
          bookingType: initialBookingType || BookingType.Hourly,
        });
      }
    },
    [initialBookingType]
  );

  React.useEffect(() => {
    handleChange(value);
  }, [handleChange, value]);

  const handleSubmit = React.useCallback(() => {
    if (!internalValue) {
      return;
    }

    if (internalValue.bookingType === BookingType.Hourly) {
      if (!internalValue.date) {
        setError("Please select a date");
      } else if (!internalValue.time) {
        setError("Please select time");
      } else if (!internalValue.duration) {
        setError("Please select duration");
      } else {
        onChange(internalValue);
      }
    } else if (internalValue.bookingType === BookingType.Daily) {
      if (!internalValue.startDate) {
        setError("Please select start date");
      } else if (askForStartTime && !internalValue.time) {
        setError("Please select time");
      } else if (!internalValue.endDate) {
        setError("Please select end date");
      } else {
        onChange(internalValue);
      }
    } else {
      onChange(internalValue);
    }
  }, [internalValue, onChange, askForStartTime]);

  const handleReset = React.useCallback(() => {
    onChange();
  }, [onChange]);

  const handleBookingTypeChange = React.useCallback(
    (bookingType: BookingType) => {
      setInternalValue({ bookingType });
    },
    []
  );

  return (
    <DateTimePickerContext.Provider
      value={{
        error,
        value: internalValue,
        onChange: handleChange,
        onSubmit: handleSubmit,
        onReset: handleReset,
        onBookingTypeChange: handleBookingTypeChange,
        onDateChange,
        askForStartTime,
        ...restProps,
      }}
    >
      {children}
    </DateTimePickerContext.Provider>
  );
}

function useDateTimePicker() {
  return React.useContext(DateTimePickerContext);
}
